import React, { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import SeoHead from "@/components/SeoHead";
import { useLang } from "@/lib/useLang";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const pickLang = (obj, key, lang) => {
  if (!obj) return "";
  if (lang === "ml") {
    const ml = obj[`${key}_ml`];
    if (ml && String(ml).trim()) return ml;
  }
  return obj[key] || "";
};

const AGE_GROUPS = ["18-24", "25-34", "35-44", "45-54", "55-64", "65+"];
const OCCUPATIONS = [
  "Student", "Salaried", "Self-employed", "Government", "Homemaker",
  "Retired", "Unemployed", "Other",
];
const DISTRICTS = [
  "Thiruvananthapuram","Kollam","Pathanamthitta","Alappuzha","Kottayam","Idukki",
  "Ernakulam","Thrissur","Palakkad","Malappuram","Kozhikode","Wayanad","Kannur","Kasaragod",
];

const PROFILE_KEY = "ks_poll_profile_v1";
const VOTER_ID_KEY = "ks_voter_id_v1";

// Safari/iOS ITP blocks third-party cookies aggressively, even SameSite=None
// ones. We mirror the server-issued voter_id into localStorage (first-party
// on the SPA's own origin) and echo it on every request as an X-Voter-Id
// header. The server prefers the cookie when present and falls back to this
// header so Chrome/Firefox/Android keep working without change.
const loadVoterId = () => {
  try { return localStorage.getItem(VOTER_ID_KEY) || ""; } catch { return ""; }
};
const saveVoterId = (v) => {
  if (!v || typeof v !== "string") return;
  try { localStorage.setItem(VOTER_ID_KEY, v); } catch { /* ignore */ }
};
// Persist the X-Voter-Id header from any response so future requests
// (including reloads) carry it forward.
const captureVoterIdFromResponse = (resp) => {
  if (!resp) return;
  const v = resp.headers?.["x-voter-id"] || resp.headers?.["X-Voter-Id"];
  if (v && /^v_[a-f0-9]{8,64}$/i.test(v)) saveVoterId(v);
};
const voterAxiosConfig = () => {
  const v = loadVoterId();
  const headers = v ? { "X-Voter-Id": v } : {};
  return { withCredentials: true, headers };
};

const loadSavedProfile = () => {
  try {
    const raw = localStorage.getItem(PROFILE_KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch { return null; }
};

const saveProfile = (p) => {
  try { localStorage.setItem(PROFILE_KEY, JSON.stringify(p)); } catch { /* ignore */ }
};

export const PollDetail = () => {
  const { slug } = useParams();
  const { lang, t } = useLang();
  const [poll, setPoll] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  const [myVote, setMyVote] = useState(null);
  const [results, setResults] = useState(null);
  const [voting, setVoting] = useState(false);
  const [pickedOption, setPickedOption] = useState("");

  // Demographic step 2
  const [profile, setProfile] = useState(() => loadSavedProfile() || {
    age_group: "", district: "", occupation: "",
    gender: "", email: "", save_profile: true, notify_me: false,
  });
  const [profileSubmitted, setProfileSubmitted] = useState(false);

  // Follow-up open answer
  const [comment, setComment] = useState("");
  const [commentSubmitting, setCommentSubmitting] = useState(false);
  const [commentSubmitted, setCommentSubmitted] = useState(false);
  const [commentErr, setCommentErr] = useState(null);

  // Approved follow-up comments (visible after voting; also auto-refreshed
  // after the visitor's own comment is approved).
  const [comments, setComments] = useState(null);

  useEffect(() => {
    if (!slug || !myVote) return undefined;
    let cancelled = false;
    axios.get(`${API}/polls/${slug}/comments`, { params: { limit: 25 } })
      .then((r) => { if (!cancelled) setComments(r.data?.items || []); })
      .catch(() => { /* silent */ });
    return () => { cancelled = true; };
  }, [slug, myVote, commentSubmitted]);

  // Share
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    setLoading(true); setErr(null);
    axios.get(`${API}/polls/${slug}`, voterAxiosConfig())
      .then((r) => {
        captureVoterIdFromResponse(r);
        setPoll(r.data);
        if (r.data?.my_vote) {
          setMyVote(r.data.my_vote);
          // fetch results to render the bars
          return axios.get(`${API}/polls/${slug}/results`).then((rr) => setResults(rr.data?.results || []));
        }
      })
      .catch((e) => setErr(e?.response?.status === 404 ? t("poll_not_found") : t("poll_load_error")))
      .finally(() => setLoading(false));
  }, [slug]);

  const handleVote = async () => {
    if (!pickedOption) return;
    setVoting(true); setErr(null);
    try {
      const r = await axios.post(`${API}/polls/${slug}/vote`,
        { option_id: pickedOption },
        voterAxiosConfig());
      captureVoterIdFromResponse(r);
      setMyVote(r.data?.my_vote);
      setResults(r.data?.results || []);
    } catch (e) {
      setErr(e?.response?.data?.detail || t("poll_vote_error_generic"));
    } finally {
      setVoting(false);
    }
  };

  const handleSubmitComment = async () => {
    if (comment.trim().length < 4) {
      setCommentErr(t("poll_followup_min"));
      return;
    }
    setCommentSubmitting(true); setCommentErr(null);
    try {
      await axios.post(`${API}/polls/${slug}/comment`,
        { text: comment.trim(), profile: profileSubmitted ? profile : null },
        voterAxiosConfig());
      setCommentSubmitted(true);
      setComment("");
    } catch (e) {
      setCommentErr(e?.response?.data?.detail || "Could not submit your response.");
    } finally {
      setCommentSubmitting(false);
    }
  };

  const handleSaveProfile = (e) => {
    e?.preventDefault?.();
    if (!profile.age_group || !profile.district || !profile.occupation) {
      return;
    }
    if (profile.save_profile) saveProfile(profile);
    setProfileSubmitted(true);
  };

  const pageUrl = useMemo(() => {
    if (typeof window === "undefined") return "";
    return `${window.location.origin}/p/${slug}`;
  }, [slug]);

  // Social crawlers (WhatsApp, iMessage, Twitter, FB) don't run our SPA's JS,
  // so they only see the static index.html OG tags. To force a per-poll
  // preview card we share the backend share-proxy URL, which serves crawler-
  // friendly HTML with the correct OG meta and refreshes real browsers to
  // the SPA route.
  const shareUrl = useMemo(() => {
    if (typeof window === "undefined") return "";
    return `${window.location.origin}/api/share/p/${slug}`;
  }, [slug]);

  const ogImageUrl = useMemo(
    () => (typeof window === "undefined" ? "" : `${window.location.origin}/api/og/poll/${slug}.png`),
    [slug],
  );

  const shareText = poll?.poll_type === "multiple_choice"
    ? t("poll_share_text_mc")
    : t("poll_share_text_open");

  const onShareWhatsApp = () => {
    const url = `https://wa.me/?text=${encodeURIComponent(`${shareText} ${shareUrl}`)}`;
    window.open(url, "_blank", "noopener");
  };
  const onShareTwitter = () => {
    const url = `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(shareUrl)}`;
    window.open(url, "_blank", "noopener");
  };
  const onCopyLink = async () => {
    try { await navigator.clipboard.writeText(shareUrl); setCopied(true); setTimeout(() => setCopied(false), 1500); } catch { /* ignore */ }
  };
  const onNativeShare = async () => {
    if (navigator.share) {
      try { await navigator.share({ title: poll?.title || "Kerala Speaks", text: shareText, url: shareUrl }); } catch { /* user cancelled */ }
    } else {
      onCopyLink();
    }
  };

  if (loading) {
    return <div className="max-w-3xl mx-auto px-5 py-20 text-center text-neutral-500">{t("poll_loading")}</div>;
  }
  if (err || !poll) {
    return (
      <div className="max-w-3xl mx-auto px-5 py-20 text-center" data-testid="poll-error">
        <p className="text-lg font-bold mb-4">{err || t("poll_not_found")}</p>
        <Link to="/" className="brutal-btn inline-block">{t("poll_back_home")}</Link>
      </div>
    );
  }

  const hasVoted = !!myVote;
  const isMC = poll.poll_type === "multiple_choice";
  // Localized projections
  const headline = pickLang(poll, "headline", lang) || pickLang(poll, "title", lang);
  const subtext = pickLang(poll, "subtext", lang);
  const title = pickLang(poll, "title", lang);
  const notice = pickLang(poll, "notice", lang);
  const category = pickLang(poll, "category", lang);
  const followUpQ = pickLang(poll, "follow_up_question", lang);

  return (
    <div className="App min-h-screen" data-testid="poll-page">
      <SeoHead
        title={`${title || headline} · Kerala Speaks`}
        description={subtext}
        canonical={`/p/${poll.slug}`}
        ogImage={`/api/og/poll/${poll.slug}.png`}
        ogType="article"
      />

      <section className="max-w-3xl mx-auto px-5 sm:px-6 md:px-12 pt-10 md:pt-16 pb-6">
        <Link to="/" className="text-xs uppercase font-bold tracking-[0.3em] text-neutral-500 hover:opacity-80" data-testid="poll-back">
          {t("poll_back")}
        </Link>
        <p className="text-xs md:text-sm uppercase font-bold tracking-[0.4em] text-neutral-600 mt-6 mb-3" data-testid="poll-eyebrow">
          ★ {category || t("poll_eyebrow_fallback")} ★
        </p>
        <h1
          className="font-display uppercase leading-[0.9] tracking-tighter"
          style={{ fontSize: "clamp(2rem, 6vw, 4rem)" }}
          data-testid="poll-headline"
        >
          {headline}
        </h1>
        <p className="mt-5 text-base md:text-lg font-medium leading-relaxed" data-testid="poll-subtext">
          {subtext}
        </p>
        {notice && (
          <div className="mt-6 border-l-4 px-4 py-3 text-sm bg-[#FFF6E5]" style={{ borderColor: "#FF9933" }} data-testid="poll-notice">
            {notice}
          </div>
        )}
      </section>

      <main className="max-w-3xl mx-auto px-5 sm:px-6 md:px-12 pb-20 space-y-8">
        {isMC && !hasVoted && (
          <section className="brutal-card p-5 md:p-7" data-testid="poll-vote-section">
            <h2 className="font-display uppercase tracking-tight text-xl md:text-2xl mb-4">
              {t("poll_cast_vote_title")}
            </h2>
            <div className="space-y-3">
              {poll.options.map((opt) => {
                const active = pickedOption === opt.id;
                const label = (lang === "ml" && opt.label_ml) ? opt.label_ml : opt.label;
                return (
                  <button
                    key={opt.id}
                    type="button"
                    onClick={() => setPickedOption(opt.id)}
                    data-testid={`poll-option-${opt.id}`}
                    className="w-full text-left border-2 px-4 py-3 hover:opacity-95 transition-opacity"
                    style={{
                      borderColor: active ? "#FF9933" : "#1f1f1f",
                      background: active ? "rgba(255,153,51,0.10)" : "transparent",
                      color: "#0A0A0A",
                    }}
                  >
                    <span className="text-sm md:text-base font-medium">{label}</span>
                  </button>
                );
              })}
            </div>
            {err && <p className="mt-3 text-sm text-red-600" data-testid="poll-vote-error">{err}</p>}
            <button
              type="button"
              onClick={handleVote}
              disabled={!pickedOption || voting}
              data-testid="poll-vote-submit"
              className="brutal-btn mt-5"
              style={{ background: "#FF9933", color: "#0A0A0A", borderColor: "#FF9933" }}
            >
              {voting ? t("poll_vote_submitting") : t("poll_vote_submit")}
            </button>
          </section>
        )}

        {isMC && hasVoted && results && (
          <section className="brutal-card p-5 md:p-7" data-testid="poll-results">
            <h2 className="font-display uppercase tracking-tight text-xl md:text-2xl">
              {t("poll_results_title")}
            </h2>
            <p className="text-xs uppercase font-bold tracking-[0.3em] text-neutral-500 mt-1 mb-4">
              {t("poll_results_sub")(results.reduce((s, r) => s + r.votes, 0))}
            </p>
            <div className="space-y-3">
              {results.map((r) => {
                const mine = r.id === myVote;
                // Find the option's localized label, fall back to results label
                const optObj = poll.options?.find((o) => o.id === r.id);
                const label = optObj
                  ? ((lang === "ml" && optObj.label_ml) ? optObj.label_ml : optObj.label)
                  : r.label;
                return (
                  <div key={r.id} data-testid={`poll-result-${r.id}`}>
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm md:text-base font-medium" style={{ color: mine ? "#0A0A0A" : "#1f1f1f" }}>
                        {label} {mine && <span className="text-[10px] uppercase font-bold tracking-[0.3em]" style={{ color: "#FF9933" }}>{t("poll_result_your_vote")}</span>}
                      </span>
                      <span className="text-sm font-bold">{r.percentage.toFixed(1)}% · {r.votes}</span>
                    </div>
                    <div className="h-3 bg-neutral-200 border-2 border-[#1f1f1f]">
                      <div className="h-full" style={{
                        width: `${Math.min(100, r.percentage)}%`,
                        background: mine ? "#FF9933" : "#0A0A0A",
                        transition: "width 0.6s ease",
                      }} />
                    </div>
                  </div>
                );
              })}
            </div>
          </section>
        )}

        {isMC && hasVoted && !profileSubmitted && (
          <section className="brutal-card p-5 md:p-7" data-testid="poll-profile">
            <h2 className="font-display uppercase tracking-tight text-xl md:text-2xl">
              {t("poll_profile_title")}
            </h2>
            <p className="text-xs uppercase font-bold tracking-[0.3em] text-neutral-500 mt-1 mb-4">
              {t("poll_profile_sub")}
            </p>
            <form onSubmit={handleSaveProfile} className="space-y-3">
              <ProfileField label={t("poll_profile_age")}>
                <select required value={profile.age_group} onChange={(e) => setProfile((p) => ({...p, age_group: e.target.value}))}
                  className="border-2 border-[#1f1f1f] px-3 py-2 w-full bg-white" data-testid="poll-profile-age">
                  <option value="">{t("poll_profile_select")}</option>
                  {AGE_GROUPS.map((g) => <option key={g} value={g}>{g}</option>)}
                </select>
              </ProfileField>
              <ProfileField label={t("poll_profile_district")}>
                <select required value={profile.district} onChange={(e) => setProfile((p) => ({...p, district: e.target.value}))}
                  className="border-2 border-[#1f1f1f] px-3 py-2 w-full bg-white" data-testid="poll-profile-district">
                  <option value="">{t("poll_profile_select")}</option>
                  {DISTRICTS.map((d) => <option key={d} value={d}>{d}</option>)}
                </select>
              </ProfileField>
              <ProfileField label={t("poll_profile_occupation")}>
                <select required value={profile.occupation} onChange={(e) => setProfile((p) => ({...p, occupation: e.target.value}))}
                  className="border-2 border-[#1f1f1f] px-3 py-2 w-full bg-white" data-testid="poll-profile-occupation">
                  <option value="">{t("poll_profile_select")}</option>
                  {OCCUPATIONS.map((o) => <option key={o} value={o}>{o}</option>)}
                </select>
              </ProfileField>
              <ProfileField label={t("poll_profile_gender")}>
                <select value={profile.gender} onChange={(e) => setProfile((p) => ({...p, gender: e.target.value}))}
                  className="border-2 border-[#1f1f1f] px-3 py-2 w-full bg-white" data-testid="poll-profile-gender">
                  <option value="">{t("poll_profile_prefer_not_say")}</option>
                  <option value="female">{t("poll_profile_female")}</option>
                  <option value="male">{t("poll_profile_male")}</option>
                  <option value="non-binary">{t("poll_profile_non_binary")}</option>
                </select>
              </ProfileField>
              <ProfileField label={t("poll_profile_email")}>
                <input type="email" value={profile.email} onChange={(e) => setProfile((p) => ({...p, email: e.target.value}))}
                  placeholder="you@example.com" className="border-2 border-[#1f1f1f] px-3 py-2 w-full bg-white" data-testid="poll-profile-email" />
              </ProfileField>
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={profile.save_profile} onChange={(e) => setProfile((p) => ({...p, save_profile: e.target.checked}))} data-testid="poll-profile-save" />
                {t("poll_profile_save_me")}
              </label>
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={profile.notify_me} onChange={(e) => setProfile((p) => ({...p, notify_me: e.target.checked}))} data-testid="poll-profile-notify" />
                {t("poll_profile_notify_me")}
              </label>
              <div className="flex gap-3 pt-2">
                <button type="submit" className="brutal-btn" data-testid="poll-profile-submit"
                  style={{ background: "#0A0A0A", color: "#FDFBF7", borderColor: "#0A0A0A" }}>
                  {t("poll_profile_submit")}
                </button>
                <button type="button" onClick={() => setProfileSubmitted(true)} className="brutal-btn-ghost text-sm" data-testid="poll-profile-skip">
                  {t("poll_profile_skip")}
                </button>
              </div>
            </form>
          </section>
        )}

        {hasVoted && profileSubmitted && followUpQ && !commentSubmitted && (
          <section className="brutal-card p-5 md:p-7" data-testid="poll-followup">
            <h2 className="font-display uppercase tracking-tight text-xl md:text-2xl">
              {followUpQ}
            </h2>
            <textarea
              rows={5}
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              maxLength={1200}
              placeholder={t("poll_followup_placeholder")}
              className="mt-4 border-2 border-[#1f1f1f] px-3 py-2 w-full bg-white"
              data-testid="poll-followup-text"
            />
            {commentErr && <p className="mt-2 text-sm text-red-600" data-testid="poll-followup-error">{commentErr}</p>}
            <p className="mt-2 text-[10px] uppercase font-bold tracking-[0.3em] text-neutral-500">
              {t("poll_followup_count")(comment.length)}
            </p>
            <button
              type="button"
              onClick={handleSubmitComment}
              disabled={commentSubmitting || comment.trim().length < 4}
              className="brutal-btn mt-4"
              style={{ background: "#FF9933", color: "#0A0A0A", borderColor: "#FF9933" }}
              data-testid="poll-followup-submit"
            >
              {commentSubmitting ? t("poll_followup_submitting") : t("poll_followup_submit")}
            </button>
          </section>
        )}

        {commentSubmitted && (
          <section className="brutal-card p-5 md:p-7" data-testid="poll-followup-thanks" style={{ background: "#0A0A0A", color: "#FDFBF7" }}>
            <h2 className="font-display uppercase tracking-tight text-xl md:text-2xl" style={{ color: "#FF9933" }}>
              {t("poll_followup_thanks_title")}
            </h2>
            <p className="text-sm mt-2 opacity-90">
              {t("poll_followup_thanks_body")}
            </p>
          </section>
        )}

        {/* "What other citizens are saying" — appears below the Help-us-
            understand profile block + follow-up writer, and just above
            the "Invite others to speak up" share section, so visitors first
            tell us about themselves and write their own response, then read
            the community, and finally are invited to share. */}
        {isMC && hasVoted && comments && comments.length > 0 && (
          <section className="brutal-card p-5 md:p-7" data-testid="poll-comments-section">
            <h2 className="font-display uppercase tracking-tight text-xl md:text-2xl">
              {t("poll_voices_title")}
            </h2>
            <p className="text-xs uppercase font-bold tracking-[0.3em] text-neutral-500 mt-1 mb-4">
              {t("poll_voices_sub")(comments.length)}
            </p>
            <ul className="space-y-3" data-testid="poll-comments-list">
              {comments.map((c, i) => {
                const meta = [c.profile?.district, c.profile?.age_group, c.profile?.occupation]
                  .filter(Boolean).join(" \u00b7 ");
                return (
                  <li key={c.id || i} className="border-l-2 pl-3" style={{ borderColor: c.status === "featured" ? "#FF9933" : "#1f1f1f" }} data-testid={`poll-comment-${i}`}>
                    {c.status === "featured" && (
                      <p className="text-[10px] uppercase font-extrabold tracking-[0.3em] mb-1" style={{ color: "#FF9933" }}>
                        {t("poll_voices_featured")}
                      </p>
                    )}
                    <p className="text-sm text-[#1f1f1f] leading-relaxed">{c.text}</p>
                    {meta && (
                      <p className="text-[10px] uppercase tracking-[0.3em] text-neutral-500 mt-1">{meta}</p>
                    )}
                  </li>
                );
              })}
            </ul>
          </section>
        )}

        {hasVoted && (
          <section className="brutal-card p-5 md:p-7" data-testid="poll-share-section">
            <h2 className="font-display uppercase tracking-tight text-xl md:text-2xl">
              {t("poll_share_title")}
            </h2>
            <p className="text-sm md:text-base mt-2 text-neutral-700">
              {t("poll_share_sub")}
            </p>
            <div className="mt-5 flex flex-wrap gap-3">
              <button onClick={onShareWhatsApp} className="brutal-btn" data-testid="poll-share-whatsapp"
                style={{ background: "#25D366", color: "#0A0A0A", borderColor: "#0A0A0A" }}>
                {t("poll_share_whatsapp")}
              </button>
              <button onClick={onShareTwitter} className="brutal-btn" data-testid="poll-share-twitter"
                style={{ background: "#0A0A0A", color: "#FDFBF7", borderColor: "#0A0A0A" }}>
                {t("poll_share_twitter")}
              </button>
              <button onClick={onCopyLink} className="brutal-btn-ghost" data-testid="poll-share-copy">
                {copied ? t("poll_share_copied") : t("poll_share_copy")}
              </button>
              {typeof navigator !== "undefined" && navigator.share && (
                <button onClick={onNativeShare} className="brutal-btn-ghost" data-testid="poll-share-native">
                  {t("poll_share_native")}
                </button>
              )}
            </div>
            <div className="mt-5 border-2 border-[#1f1f1f] p-4 bg-[#FFF6E5]">
              <p className="text-xs uppercase font-bold tracking-[0.3em] text-neutral-600 mb-2">{t("poll_share_image_eyebrow")}</p>
              <a
                href={`${API}/og/poll/${slug}.png`}
                target="_blank" rel="noopener noreferrer"
                className="text-sm underline" data-testid="poll-share-image-link"
              >
                {t("poll_share_image_link")}
              </a>
            </div>
          </section>
        )}

        {!isMC && (
          <section className="brutal-card p-5 md:p-7" data-testid="poll-open-cta">
            <h2 className="font-display uppercase tracking-tight text-xl md:text-2xl">
              {t("poll_open_cta_title")}
            </h2>
            <p className="text-sm md:text-base mt-2">
              {t("poll_open_cta_sub")}
            </p>
            <Link to="/" className="brutal-btn inline-block mt-4" data-testid="poll-open-home"
              style={{ background: "#FF9933", color: "#0A0A0A", borderColor: "#FF9933" }}>
              {t("poll_open_cta_btn")}
            </Link>
          </section>
        )}
      </main>
    </div>
  );
};

const ProfileField = ({ label, children }) => (
  <label className="block">
    <span className="block text-[10px] uppercase font-bold tracking-[0.3em] text-neutral-600 mb-1">{label}</span>
    {children}
  </label>
);

export default PollDetail;
